import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Megaphone, Plus, Pin, Eye, EyeOff, Pencil, Trash2, FileText, Youtube,
  Upload, X, CalendarClock, Loader2, BellOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { CATEGORIES, CATEGORY_STYLES, getYouTubeId, fmtDate } from '../components/announcementUtils';
import AnnouncementPdfViewer from '../components/AnnouncementPdfViewer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = { title: '', body: '', category: 'News', pdf_url: null, pdf_name: null, youtube_url: '', expiry_date: '', pinned: false, notify: true };

export default function AnnouncementsPage() {
  const { getAuthHeader } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [detail, setDetail] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const openedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${API}/announcements`, { headers: getAuthHeader() });
    if (r.ok) {
      const d = await r.json();
      setItems(d.announcements || []);
      setCanManage(!!d.can_manage);
    }
    setLoading(false);
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  // Opening the Announcements tab clears the sidebar "new" dot by marking all
  // currently-visible announcements as seen for this user.
  useEffect(() => {
    fetch(`${API}/announcements/mark-all-seen`, { method: 'POST', headers: getAuthHeader() }).catch(() => {});
  }, [getAuthHeader]);

  const markSeen = useCallback(async (id) => {
    try { await fetch(`${API}/announcements/${id}/seen`, { method: 'POST', headers: getAuthHeader() }); }
    catch { /* ignore */ }
  }, [getAuthHeader]);

  const openDetail = useCallback((a) => {
    setDetail(a);
    markSeen(a.announcement_id);
    setItems(prev => prev.map(x => x.announcement_id === a.announcement_id ? { ...x, seen: true } : x));
  }, [markSeen]);

  // Deep-link: /announcements?open=ANN-xxxx
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId && !openedRef.current && items.length) {
      const a = items.find(x => x.announcement_id === openId);
      if (a) { openedRef.current = true; openDetail(a); searchParams.delete('open'); setSearchParams(searchParams, { replace: true }); }
    }
  }, [items, searchParams, setSearchParams, openDetail]);

  const filtered = items.filter(a => filter === 'All' || a.category === filter);

  const openComposer = (a = null) => {
    if (a) {
      setEditingId(a.announcement_id);
      setForm({
        title: a.title, body: a.body, category: a.category,
        pdf_url: a.pdf_url, pdf_name: a.pdf_name, youtube_url: a.youtube_url || '',
        expiry_date: a.expiry_date ? a.expiry_date.slice(0, 10) : '', pinned: !!a.pinned, notify: true,
      });
    } else {
      setEditingId(null);
      setForm(emptyForm);
    }
    setComposerOpen(true);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API}/upload`, { method: 'POST', headers: getAuthHeader(), body: fd });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setForm(f => ({ ...f, pdf_url: d.url, pdf_name: d.filename }));
      toast.success('PDF uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error('Title and body are required'); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(), body: form.body.trim(), category: form.category,
        pdf_url: form.pdf_url || null, pdf_name: form.pdf_name || null,
        youtube_url: form.youtube_url.trim() || null,
        expiry_date: form.expiry_date || null, pinned: form.pinned,
        notify: editingId ? true : !!form.notify,
      };
      const url = editingId ? `${API}/announcements/${editingId}` : `${API}/announcements`;
      const method = editingId ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Failed'); }
      toast.success(editingId ? 'Announcement updated' : 'Announcement published');
      setComposerOpen(false);
      load();
    } catch (err) { toast.error(err.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const toggleVisibility = async (a) => {
    const r = await fetch(`${API}/announcements/${a.announcement_id}/toggle-visibility`, {
      method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ hidden: !a.hidden }),
    });
    if (r.ok) { toast.success(!a.hidden ? 'Hidden from all surfaces' : 'Now visible'); load(); }
  };

  const togglePin = async (a) => {
    const r = await fetch(`${API}/announcements/${a.announcement_id}/toggle-pin`, {
      method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ pinned: !a.pinned }),
    });
    if (r.ok) { toast.success(!a.pinned ? 'Pinned to top' : 'Unpinned'); load(); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    const r = await fetch(`${API}/announcements/${a.announcement_id}`, { method: 'DELETE', headers: getAuthHeader() });
    if (r.ok) { toast.success('Announcement deleted'); load(); }
  };

  const detailYt = detail && getYouTubeId(detail.youtube_url);

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="announcements-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Megaphone className="h-6 w-6" /> Announcements</h1>
            <p className="text-muted-foreground">Company news, policies, guides and resources.</p>
          </div>
          {canManage && (
            <Button onClick={() => openComposer()} data-testid="new-announcement-btn"><Plus className="h-4 w-4 mr-1.5" /> New Announcement</Button>
          )}
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-2" data-testid="category-filter">
          {['All', ...CATEGORIES].map(c => (
            <Button key={c} size="sm" variant={filter === c ? 'default' : 'outline'} onClick={() => setFilter(c)} data-testid={`filter-${c.toLowerCase()}`}>{c}</Button>
          ))}
        </div>

        {loading ? <p className="text-muted-foreground">Loading...</p>
          : filtered.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground" data-testid="announcements-empty">
              <Megaphone className="h-10 w-10 mx-auto mb-3 opacity-40" /><p>No announcements{filter !== 'All' ? ` in ${filter}` : ''} yet.</p>
            </CardContent></Card>
          ) : (
            <div className="flex flex-col gap-2" data-testid="announcements-list">
              {filtered.map(a => {
                const yt = getYouTubeId(a.youtube_url);
                return (
                  <Card key={a.announcement_id} className={`w-full ${a.hidden ? 'opacity-60' : ''}`} data-testid={`announcement-card-${a.announcement_id}`}>
                    <CardContent className="py-3 px-4 space-y-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={CATEGORY_STYLES[a.category]}>{a.category}</Badge>
                        {a.is_new && <Badge className="bg-red-600 text-white hover:bg-red-600" data-testid={`new-tag-${a.announcement_id}`}>NEW</Badge>}
                        {a.pinned && <Pin className="h-3.5 w-3.5 text-primary fill-primary" data-testid={`pin-icon-${a.announcement_id}`} />}
                        {a.hidden && <Badge variant="secondary">Hidden</Badge>}
                        {a.expired && <Badge variant="secondary">Expired</Badge>}
                        <span className="ml-auto text-xs text-muted-foreground">{fmtDate(a.publish_date)}</span>
                      </div>
                      <CardTitle className="text-base leading-snug cursor-pointer hover:underline" onClick={() => openDetail(a)} data-testid={`announcement-title-${a.announcement_id}`}>{a.title}</CardTitle>
                      <p className="text-sm text-muted-foreground line-clamp-2">{a.body}</p>
                      <div className="flex items-center gap-3 flex-wrap pt-0.5">
                        {a.pdf_url && <span className="flex items-center gap-1 text-xs text-muted-foreground"><FileText className="h-3.5 w-3.5" /> PDF</span>}
                        {yt && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Youtube className="h-3.5 w-3.5" /> Video</span>}
                        <Button size="sm" variant="outline" className="h-7 px-2.5" onClick={() => openDetail(a)} data-testid={`read-more-${a.announcement_id}`}>Read more</Button>
                        {canManage && (
                          <div className="ml-auto flex items-center gap-0.5">
                            <Button size="icon" variant="ghost" className="h-7 w-7" title="Edit" onClick={() => openComposer(a)} data-testid={`edit-${a.announcement_id}`}><Pencil className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" title={a.pinned ? 'Unpin' : 'Pin'} onClick={() => togglePin(a)} data-testid={`toggle-pin-${a.announcement_id}`}><Pin className={`h-4 w-4 ${a.pinned ? 'fill-primary text-primary' : ''}`} /></Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" title={a.hidden ? 'Show' : 'Hide'} onClick={() => toggleVisibility(a)} data-testid={`toggle-visibility-${a.announcement_id}`}>{a.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" title="Delete" onClick={() => remove(a)} data-testid={`delete-${a.announcement_id}`}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="w-[96vw] max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="announcement-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={CATEGORY_STYLES[detail.category]}>{detail.category}</Badge>
                  {detail.is_new && <Badge className="bg-red-600 text-white hover:bg-red-600">NEW</Badge>}
                  <span className="text-xs text-muted-foreground ml-auto">{fmtDate(detail.publish_date)}</span>
                </div>
                <DialogTitle className="text-xl mt-1">{detail.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-5">
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{detail.body}</p>
                {detailYt && (
                  <div className="aspect-video w-full rounded-lg overflow-hidden border" data-testid="detail-youtube">
                    <iframe title="video" className="w-full h-full" src={`https://www.youtube.com/embed/${detailYt}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  </div>
                )}
                {detail.pdf_url && (
                  <div className="space-y-2" data-testid="detail-pdf">
                    <span className="text-sm font-medium flex items-center gap-2"><FileText className="h-4 w-4" /> {detail.pdf_name || 'Document.pdf'}</span>
                    <AnnouncementPdfViewer url={detail.pdf_url} />
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Composer dialog */}
      <Dialog open={composerOpen} onOpenChange={(o) => !o && setComposerOpen(false)}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="announcement-composer">
          <DialogHeader><DialogTitle>{editingId ? 'Edit Announcement' : 'New Announcement'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Announcement title" data-testid="composer-title" />
            </div>
            <div>
              <Label>Body / Description *</Label>
              <Textarea rows={5} value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} placeholder="Write the announcement..." data-testid="composer-body" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="composer-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Expiry date (optional)</Label>
                <Input type="date" value={form.expiry_date} onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} data-testid="composer-expiry" />
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-1"><Youtube className="h-3.5 w-3.5" /> YouTube link (optional)</Label>
              <Input value={form.youtube_url} onChange={e => setForm(f => ({ ...f, youtube_url: e.target.value }))} placeholder="https://youtube.com/watch?v=..." data-testid="composer-youtube" />
            </div>
            <div>
              <Label className="flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> PDF attachment (optional)</Label>
              {form.pdf_url ? (
                <div className="flex items-center justify-between border rounded-lg p-2.5 text-sm">
                  <span className="flex items-center gap-2 truncate"><FileText className="h-4 w-4" /> {form.pdf_name || 'Document.pdf'}</span>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setForm(f => ({ ...f, pdf_url: null, pdf_name: null }))} data-testid="composer-remove-pdf"><X className="h-4 w-4" /></Button>
                </div>
              ) : (
                <div>
                  <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={onUpload} data-testid="composer-pdf-input" />
                  <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="composer-upload-btn">
                    {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />} Upload PDF
                  </Button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.pinned} onCheckedChange={v => setForm(f => ({ ...f, pinned: v }))} data-testid="composer-pin" />
              <Label className="flex items-center gap-1 cursor-pointer"><Pin className="h-3.5 w-3.5" /> Pin to top</Label>
            </div>
            {!editingId && (
              <div className="flex items-start gap-2 rounded-lg border p-3 bg-muted/30">
                <Switch checked={!form.notify} onCheckedChange={v => setForm(f => ({ ...f, notify: !v }))} data-testid="composer-suppress-email" className="mt-0.5" />
                <div className="space-y-0.5">
                  <Label className="flex items-center gap-1 cursor-pointer"><BellOff className="h-3.5 w-3.5" /> Don't send email notification</Label>
                  <p className="text-xs text-muted-foreground">Publish silently. When off, all employees get an email about this announcement.</p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setComposerOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="composer-submit">
              {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{editingId ? 'Save Changes' : 'Publish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
